import {
  Typography,
  Button,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActionArea,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect, useRef } from "react";
import { motion } from "motion/react";
import FuseLoading from "@fuse/core/FuseLoading";
import PlansSkeleton from "@/components/shared/PlansSkeleton";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import {
  useGetPlansListQuery,
  useCheckoutPlanMutation,
  useCheckSessionStatusMutation,
  useUpgradePlanMutation,
} from "@/store/services/plans";
import { useGetAddressesListQuery } from "@/store/services/address";
import { toast } from "react-toastify";
import { Check } from "@mui/icons-material";
import { getPlan, getUser } from "@/utils/localStorage";
import useGetCurrentPlan from "../utilities/hooks/useGetCurrentPlan";
import {
  Plan,
  PlanInterval,
  PLAN_PERKS,
  PLAN_TAGLINES,
} from "../utilities/constants/constants";
import {
  APIErrorResponse,
  isFetchBaseQueryError,
  isSerializedError,
} from "@/utils/types";

const DOWNGRADE_ADDRESS_STORAGE_KEY = "billing_downgrade_address_id";

function getPlansLoadErrorMessage(error: unknown): string {
  if (isFetchBaseQueryError(error)) {
    const data = error.data as APIErrorResponse | undefined;
    return (
      data?.errors?.[0]?.title ||
      data?.errors?.[0]?.detail ||
      "Something went wrong please try again later."
    );
  }

  if (isSerializedError(error) && error.message) {
    return error.message;
  }

  return "Something went wrong please try again later";
}

const getAddressTypeLabel = (type: string | null) =>
  !type ? "N/A" : type.charAt(0).toUpperCase() + type.slice(1);

const getAddressTypeColor = (
  type: string | null,
): "primary" | "success" | "warning" | "default" => {
  if (!type) return "default";
  const map: Record<string, "primary" | "success" | "warning"> = {
    apartment: "primary",
    house: "success",
    office: "warning",
    residential: "primary",
  };
  return map[type] || "default";
};

type Address = {
	id: number;
	name: string | null;
	location_name: string | null;
	type: string | null;
	application?: { id: number } | null;
};

function PlansPage() {
  // Get plan from localStorage instead of cookies
  const currentPlan = getPlan();

  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const sessionId = searchParams.get("session_id");
  const {
    data: plansResponse,
    isLoading: isLoadingPlans,
    isFetching: isFetchingPlans,
    error: plansError,
    refetch: refetchPlans,
  } = useGetPlansListQuery(undefined);
  const { currentPlanInfo, isLoading: isLoadingCurrentPlan } =
    useGetCurrentPlan();
  const [checkout] = useCheckoutPlanMutation();
  const [upgrade] = useUpgradePlanMutation();
  const [checkSessionStatus, { isLoading: isCheckoutStatusLoading }] =
    useCheckSessionStatusMutation();
  const [loadingPlanId, setLoadingPlanId] = useState<number | null>(null);
  const [billingInterval, setBillingInterval] =
    useState<PlanInterval>("Monthly");

  const [downgradeModalOpen, setDowngradeModalOpen] = useState(false);
  const [pendingDowngradePlan, setPendingDowngradePlan] = useState<{
    planId: number;
    planNickname: string;
    newNumberOfApplications: number;
  } | null>(null);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);

  const { data: addressesResponse, isLoading: isLoadingAddresses } = useGetAddressesListQuery(
    {},
    { skip: !downgradeModalOpen },
  );
  const addresses: Address[] = addressesResponse?.data || [];
  const hasCheckSessionRun = useRef(false);

  // Always hit `/api/plan` when this page is shown (not only from RTK cache).
  useEffect(() => {
    void refetchPlans();
  }, [refetchPlans]);

  // Set default billing interval from current plan when plans data loads
  useEffect(() => {
    if (currentPlanInfo?.interval) {
      setBillingInterval(
        (currentPlanInfo.interval as PlanInterval) || "Monthly",
      );
    }
  }, [currentPlanInfo]);

  // Update plan in localStorage to empty object when currentPlanInfo is null
  useEffect(() => {
    if (!isLoadingCurrentPlan && currentPlanInfo === null) {
      import("@/utils/localStorage").then(({ setPlan }) => {
        setPlan({});
      });
    }
  }, [currentPlanInfo, isLoadingCurrentPlan]);

  const cleanupSessionId = (onComplete?: () => void) => {
    setTimeout(() => {
      const params = new URLSearchParams(searchParams.toString());
      params.delete("session_id");

      const newUrl = params.toString()
        ? `${pathname}?${params.toString()}`
        : pathname;

      navigate(newUrl, { replace: true });
      onComplete?.();
    }, 1000);
  };

  useEffect(() => {
    if (!sessionId || hasCheckSessionRun.current) return;

    const runCheckSession = async () => {
      hasCheckSessionRun.current = true;
      const addressIdStr = sessionStorage.getItem(DOWNGRADE_ADDRESS_STORAGE_KEY);
      const addressId = addressIdStr ? parseInt(addressIdStr, 10) : undefined;

      try {
        const result = await checkSessionStatus({
          session_id: sessionId,
          ...(addressId && !Number.isNaN(addressId) ? { address_id: addressId } : {}),
        }).unwrap();

        sessionStorage.removeItem(DOWNGRADE_ADDRESS_STORAGE_KEY);

        const status = result?.data?.status || "failed";

        if (status === "complete") {
          toast.success("Payment successful! Thank you for your purchase.");
          const refreshedPlansResult = await refetchPlans();
          const latestPlans =
            refreshedPlansResult?.data?.data || plansResponse?.data || [];
          const latestCurrentPlan = latestPlans.find(
            (plan: Plan) => plan.isCurrentPlan === true,
          );

          if (latestCurrentPlan) {
            const existingPlan =
              currentPlan && typeof currentPlan === "object" ? currentPlan : {};
            const updatedPlanData = {
              ...existingPlan,
              id: latestCurrentPlan.id,
              name: latestCurrentPlan.nickname,
              interval: latestCurrentPlan.interval,
              number_of_applications: latestCurrentPlan.number_of_applications,
            };

            import("@/utils/localStorage").then(({ setPlan }) => {
              setPlan(updatedPlanData);
            });
          }
        } else {
          toast.error("Payment failed. Please try again or contact support.");
        }
      } catch (statusError: unknown) {
        sessionStorage.removeItem(DOWNGRADE_ADDRESS_STORAGE_KEY);
        const errorData =
          statusError &&
          typeof statusError === "object" &&
          "data" in statusError &&
          typeof (statusError as { data?: unknown }).data === "object"
            ? ((statusError as { data: { errors?: { detail?: string }[] } }).data
                ?.errors?.[0]?.detail)
            : null;
        const errorMessage =
          errorData ||
          "Something went wrong while verifying payment. Please contact support.";
        toast.error(errorMessage);
      } finally {
        cleanupSessionId(() => navigate("/dashboard"));
      }
    };

    runCheckSession();
  }, [sessionId, refetchPlans, plansResponse?.data, currentPlan, pathname, navigate, searchParams]);

  // Filter plans based on selected billing interval
  const filteredPlans = useMemo(() => {
    if (!plansResponse?.data) return [];

    return plansResponse.data.filter(
      (plan) => plan.interval === billingInterval,
    );
  }, [plansResponse?.data, billingInterval]);

  // Group plans by nickname to show Basic and Pro side-by-side
  const groupedPlans = useMemo(() => {
    const grouped: Record<string, Plan[]> = {};
    filteredPlans.forEach((plan) => {
      if (!grouped[plan.nickname]) {
        grouped[plan.nickname] = [];
      }

      grouped[plan.nickname].push(plan);
    });

    return Object.entries(grouped).map(([nickname, plans]) => {
      const planData = plans[0];
      // Use the isCurrentPlan and isUpgradeable directly from the API response
      const isCurrentPlan = planData.isCurrentPlan || false;
      const isUpgradeable = planData.isUpgradeable || false;

      return {
        nickname,
        plans: {
          ...planData,
          isCurrentPlan,
          isUpgradeable,
        },
      };
    });
  }, [filteredPlans]);

  const hasAnyCurrentPlan = useMemo(
    () => filteredPlans.some((p) => p.isCurrentPlan),
    [filteredPlans],
  );

  const handlePlanClick = (
    plan: { id: number; nickname: string; number_of_applications?: number },
    buttonText: string,
  ) => {
    if (buttonText === "Current Plan") return;

    const user = getUser();
    const totalNumberOfAddresses = Number(user?.total_number_of_addresses ?? 0);
    const planApplications = plan.number_of_applications ?? 0;

    // Downgrade / checkout flow: user must pick an address to disable when they have more
    // addresses than the selected plan allows (e.g. 2 addresses, plan allows 1 application).
    if (
      planApplications > 0 &&
      totalNumberOfAddresses > 0 &&
      totalNumberOfAddresses > planApplications
    ) {
      setPendingDowngradePlan({
        planId: plan.id,
        planNickname: plan.nickname,
        newNumberOfApplications: planApplications,
      });
      setSelectedAddressId(null);
      setDowngradeModalOpen(true);
      return;
    }

    const actionType =
      buttonText === "Upgrade Plan" ? "Upgrade" : "Purchase";
    handlePlanAction(plan.id, actionType);
  };

  const handleDowngradeConfirm = async () => {
    if (!pendingDowngradePlan || !selectedAddressId) {
      toast.error("Please select an address to disable.");
      return;
    }

    try {
      setLoadingPlanId(pendingDowngradePlan.planId);
      sessionStorage.setItem(
        DOWNGRADE_ADDRESS_STORAGE_KEY,
        String(selectedAddressId),
      );

      const result = await checkout(pendingDowngradePlan.planId).unwrap();

      setDowngradeModalOpen(false);
      setPendingDowngradePlan(null);
      setSelectedAddressId(null);

      if (result?.data?.url) {
        window.location.href = result.data.url;
      } else {
        toast.success("Redirecting to checkout...");
      }
    } catch (error: unknown) {
      sessionStorage.removeItem(DOWNGRADE_ADDRESS_STORAGE_KEY);
      const errorMessage =
        (error as { data?: { errors?: { detail?: string }[] } })?.data
          ?.errors?.[0]?.detail || "Downgrade checkout failed!";
      toast.error(errorMessage);
      console.error(error);
    } finally {
      setLoadingPlanId(null);
    }
  };

  const handlePlanAction = async (
    planId: number,
    actionType: "Purchase" | "Upgrade",
  ) => {
    try {
      setLoadingPlanId(planId);

      const result =
        actionType === "Upgrade"
          ? await upgrade({ id: planId }).unwrap()
          : await checkout(planId).unwrap();

      if (result?.data?.url) {
        window.location.href = result.data.url;
      } else {
        toast.success(
          actionType === "Upgrade"
            ? "Processing upgrade..."
            : "Redirecting to checkout...",
        );
      }
    } catch (error: unknown) {
      const errorMessage =
        (error as { data?: { errors?: { detail?: string }[] } })?.data
          ?.errors?.[0]?.detail ||
        `${actionType === "Upgrade" ? "Upgrade" : "Checkout"} failed!`;
      toast.error(errorMessage);
      console.error(error);
    } finally {
      setLoadingPlanId(null);
    }
  };

  if (isLoadingPlans || isFetchingPlans || isLoadingCurrentPlan) {
    return <PlansSkeleton />;
  }

  if (plansError) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { delay: 0.1 } }}
        className="flex flex-col flex-1 items-center h-full"
      >
        <Typography className="text-gray-500 py-8">
          {getPlansLoadErrorMessage(plansError)}
        </Typography>
      </motion.div>
    );
  }

  return (
    <div className="flex min-w-0 flex-auto flex-col">
      {isCheckoutStatusLoading && (
        <Box
          className="fixed inset-0 z-50 flex items-center justify-center"
          sx={{ backgroundColor: "rgba(255, 255, 255, 0.5)" }}
        >
          <FuseLoading />
        </Box>
      )}
      <div className="w-full px-4 sm:px-8 pb-4">
        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <ToggleButtonGroup
            value={billingInterval}
            exclusive
            onChange={(e, newInterval) => {
              if (newInterval) setBillingInterval(newInterval);
            }}
            sx={{
              border: "1px solid rgba(0, 0, 0, 0.75)",
              padding: "3px",
              borderRadius: "12px",
              "& .MuiToggleButton-root": {
                color: "#000000",
                border: "none",
                textTransform: "none",
                fontSize: "13px",
                fontWeight: 600,
                padding: "6px 16px",
                transition: "all 0.3s ease",
                borderRadius: "8px",
                position: "relative",
                "&.Mui-selected": {
                  backgroundColor: "#0a74da",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#0a74da",
                  },
                },
              },
            }}
          >
            <ToggleButton value="Monthly">Monthly</ToggleButton>
            <ToggleButton value="Yearly">
              Yearly
              <Chip
                label="20% OFF"
                size="small"
                sx={{
                  position: "absolute",
                  top: "-15px",
                  right: "0",
                  backgroundColor: "#ffca00",
                  color: "#000",
                  fontWeight: 600,
                  fontSize: "11px",
                  lineHeight: "11px",
                  height: "unset",
                  borderRadius: "12px",
                  "& .MuiChip-label": {
                    padding: "4px 10px",
                  },
                }}
              />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {groupedPlans.length === 0 ? (
            <div className="col-span-full flex justify-center">
              <Typography
                variant="body2"
                className="text-gray-500 py-8"
              >
                No plans found.
              </Typography>
            </div>
          ) : (
          groupedPlans.map((planGroup) => {
            const plan = planGroup.plans;
            const isBasic = plan.nickname.includes("Basic");
            const isCurrentUserPlan = plan.isCurrentPlan;
            const isPlanUpgradeable = plan.isUpgradeable;

            // Button logic based on isCurrentPlan & isUpgradeable & currentPlanInfo
            // 1. Both false + currentPlanInfo null → "Purchase Plan" (new user, no plan)
            // 2. Both false + currentPlanInfo exists → Alert (downgrade unavailable)
            // 3. isCurrentPlan true, isUpgradeable false → "Current Plan", disabled
            // 4. isCurrentPlan false, isUpgradeable true → "Upgrade Plan" or "Purchase Plan"
            let buttonText = "Purchase Plan";
            let buttonDisabled = false;
            let showButton = true;

            if (!isCurrentUserPlan && !isPlanUpgradeable) {
              if (currentPlanInfo === null) {
                buttonText = "Purchase Plan";
                showButton = true;
                buttonDisabled = false;
              } else {
                showButton = false;
              }
            } else if (isCurrentUserPlan && !isPlanUpgradeable) {
              buttonText = "Current Plan";
              buttonDisabled = true;
            } else if (!isCurrentUserPlan && isPlanUpgradeable) {
              buttonText = hasAnyCurrentPlan ? "Upgrade Plan" : "Purchase Plan";
              buttonDisabled = false;
            }

            return (
              <Box
                key={plan.id}
                className={`relative rounded-2xl transition-all duration-300 ${
                  !isBasic ? "lg:ring-2 lg:ring-yellow-400" : ""
                }`}
                sx={{
                  background: "#ffffff",
                  border: "1px solid rgba(0, 0, 0, 0.1)",
                  "& .basicPlan ul li:last-child": {
                    opacity: 0,
                  },
                }}
              >
                {/* Most Popular Badge */}
                {!isBasic && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Chip
                      label="Most Popular"
                      sx={{
                        backgroundColor: "#ffca00",
                        color: "#000000",
                        fontWeight: 600,
                        fontSize: "12px",
                        lineHeight: "12px",
                        borderRadius: "12px",
                        height: "unset",
                        m: 0,
                        "& .MuiChip-label": {
                          padding: "7px 12px",
                        },
                      }}
                    />
                  </div>
                )}

                {/* Current Plan Badge */}
                {isCurrentUserPlan && (
                  <div className="absolute top-4 right-4">
                    <Chip
                      label="Current Plan"
                      sx={{
                        color: "#fff",
                        backgroundColor: "#0a74da",
                        fontWeight: 500,
                        fontSize: "11px",
                        lineHeight: "11px",
                        borderRadius: "12px",
                        height: "unset",
                        m: 0,
                        "& .MuiChip-label": {
                          padding: "6px 10px",
                        },
                      }}
                    />
                  </div>
                )}

                <div className={`p-8 ${isBasic ? "basicPlan" : ""}`}>
                  {/* Plan Name */}
                  <Typography className="text-3xl font-bold mb-1 text-center">
                    {plan.nickname}
                  </Typography>

                  {/* Tagline */}
                  <Typography className="text-md mb-6 text-center">
                    {PLAN_TAGLINES[plan.nickname]}
                  </Typography>

                  {/* Price */}
                  <div className="mb-6 text-center">
                    <Typography className="text-6xl font-bold">
                      ${plan.amount.toFixed(2)}
                    </Typography>
                    <Typography className="text-md mt-1">
                      per {billingInterval === "Monthly" ? "month" : "year"}
                    </Typography>
                    {billingInterval === "Yearly" && (
                      <Typography className="text-md mt-1">
                        That's ${(plan.amount / 12).toFixed(2)}/month
                      </Typography>
                    )}
                  </div>

                  {/* Perks List */}
                  <ul className="space-y-4 mb-8">
                    {PLAN_PERKS[plan.nickname]?.map((perk, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <Check
                          sx={{
                            fontSize: "20px",
                            flexShrink: 0,
                            color: "#0a74da",
                          }}
                        />
                        <Typography className="text-md">{perk}</Typography>
                      </li>
                    ))}
                  </ul>

                  {/* Action Button or Alert */}
                  {showButton ? (
                    <Button
                      fullWidth
                      color="secondary"
                      variant="contained"
                      onClick={() => handlePlanClick(plan, buttonText)}
                      disabled={buttonDisabled || loadingPlanId === plan.id}
                      startIcon={
                        loadingPlanId === plan.id && (
                          <CircularProgress size={20} color="inherit" />
                        )
                      }
                    >
                      {buttonText}
                    </Button>
                  ) : (
                    <Alert
                      severity="info"
                      sx={{
                        "& .MuiAlert-message": {
                          width: "100%",
                        },
                      }}
                    >
                      Plan downgrades are not currently available. Please
                      contact support if you need assistance.
                    </Alert>
                  )}
                </div>
              </Box>
            );
          })
          )}
        </div>
      </div>

      {/* Downgrade: Select address to disable */}
      <Dialog
        open={downgradeModalOpen}
        onClose={() => {
          setDowngradeModalOpen(false);
          setPendingDowngradePlan(null);
          setSelectedAddressId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select address to disable/deactivate</DialogTitle>
        <DialogContent>
          <Typography variant="body1" className="mb-4 text-gray-600">
            You&apos;re downgrading to {pendingDowngradePlan?.planNickname}{" "}
            which allows {pendingDowngradePlan?.newNumberOfApplications}{" "}
            application
            {pendingDowngradePlan?.newNumberOfApplications !== 1 ? "s" : ""}.
            Your current plan allows more. Please choose which address to disable
            before continuing. The selected address and its associated
            application will be disabled/deactivated respectively.
          </Typography>
          {isLoadingAddresses ? (
            <Box className="flex justify-center items-center py-4">
              <FuseLoading />
            </Box>
          ) : addresses.length === 0 ? (
            <Box className="flex flex-col items-center justify-center py-12 gap-4">
              <FuseSvgIcon className="text-red-500 text-4xl">heroicons-outline:exclamation-circle</FuseSvgIcon>
              <Typography variant="body1" className="text-gray-600">
                No addresses found. Please add an address first.
              </Typography>
            </Box>
          ) : (
            <Box className="grid grid-cols-2 gap-3">
              {addresses.map((addr: Address) => {
                const isSelected = selectedAddressId === addr.id;
                return (
                  <Card
                    key={addr.id}
                    sx={{
                      border: "2px solid",
                      borderColor: isSelected ? "primary.main" : "divider",
                      boxShadow: isSelected ? 2 : 0,
                      "&:hover": {
                        borderColor: isSelected ? "primary.main" : "primary.light",
                        boxShadow: 2,
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => setSelectedAddressId(addr.id)}
                    >
                      <CardContent>
                        <Box className="flex items-center justify-between">
                          <Box className="flex-1">
                            <Typography
                              variant="h6"
                              className="font-semibold mb-1 text-lg"
                            >
                              {addr.name || addr.location_name || `Address #${addr.id}`}
                            </Typography>
                            <Typography
                              variant="body2"
                              className="text-gray-600 mb-2"
                            >
                              {addr.location_name || " "}
                            </Typography>
                            <Chip
                              label={getAddressTypeLabel(addr.type)}
                              color={getAddressTypeColor(addr.type)}
                              size="small"
                            />
                          </Box>
                          <FuseSvgIcon className="text-gray-400">
                            heroicons-outline:chevron-right
                          </FuseSvgIcon>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
						color="secondary"
            onClick={() => {
              setDowngradeModalOpen(false);
              setPendingDowngradePlan(null);
              setSelectedAddressId(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleDowngradeConfirm}
            disabled={
              !selectedAddressId ||
              addresses.length === 0 ||
              loadingPlanId === pendingDowngradePlan?.planId
            }
            startIcon={
              loadingPlanId === pendingDowngradePlan?.planId && (
                <CircularProgress size={20} color="inherit" />
              )
            }
          >
            Continue to checkout
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default PlansPage;
